import React, { FC, Fragment } from 'react'
import { useSelector } from 'react-redux'
import { StyleSheet, View } from 'react-native'
import { color, spacing, fontSize, fontFamily, fontWeight } from '@partipost-cm-new/app-preview/dist/Styles/Base'
import { moderateScale } from '@partipost-cm-new/app-preview/dist/Utils/Scaling'
import Card from '@partipost-cm-new/app-preview/dist/Components/Card'
import PartiText from '@partipost-cm-new/app-preview/dist/Components/PartiText'
import { useNewTranslation } from 'hooks'
import { selectselectIgRepostFirstStepData } from 'state/appPreview/selector/igStoryRepost'
import SubmitPage from '../common/SubmitPage'
import MediaPreview from '../common/MediaPreview'
import Icon from 'particles/Icon'

const IGStoryRepostPreview: FC = () => {
    const [t] = useNewTranslation()
    const igStoryRepostFirstStepData = useSelector(selectselectIgRepostFirstStepData)

    return (
        <Fragment>
            <SubmitPage stepIndex={'1'} totalStepNumber={'2'} buttonText={t('button.submit')}>
                <View style={{ paddingHorizontal: spacing.medium }}>
                    <PartiText style={styles.taskType}>{igStoryRepostFirstStepData.taskTitle}</PartiText>
                    <PartiText style={styles.stepTitle}>{igStoryRepostFirstStepData.stepTitle}</PartiText>
                    <Card cardStyle={styles.container}>
                        <PartiText style={styles.title}>
                            {t('appCampaign.campaignStep.thingsToNoteHeader').toUpperCase()}
                        </PartiText>
                        <PartiText>
                            {t('appCampaign.campaignStep.thingsToNoteSaveOriginalPhotoVideoInstructions')}
                        </PartiText>
                    </Card>
                    <Card cardStyle={styles.container}>
                        <PartiText style={styles.title}>
                            {t('appCampaign.joinCampaign.submitContent.submissionRequirementHeader')}
                        </PartiText>
                        <PartiText>{igStoryRepostFirstStepData.submissionRequirementDescription}</PartiText>
                        <View style={styles.divider}></View>
                        <View style={styles.requirementSectionHeader}>
                            <Icon size="24px" name="instagramLogo" color="primary" />
                            <PartiText style={{ ...styles.header, padding: 0 }}>
                                {t('appCampaign.campaignStep.storyRequirementHeader')}
                            </PartiText>
                        </View>
                        <PartiText style={styles.header}>{t('appCampaign.campaignStep.dosHeader')}</PartiText>
                        <PartiText>{igStoryRepostFirstStepData.mediaDos}</PartiText>
                        <PartiText style={styles.header}>{t('appCampaign.campaignStep.dontsHeader')}</PartiText>
                        <PartiText>{igStoryRepostFirstStepData.mediaDonts}</PartiText>
                        <View style={styles.divider}></View>
                        <PartiText style={styles.header}>{t('appCampaign.campaignStep.requiredTagsHeader')}</PartiText>
                        <View style={styles.hashtagContainer}>
                            {igStoryRepostFirstStepData.profileTags?.map((tag, index) =>
                                tag?.value ? (
                                    <PartiText style={styles.hashtag} key={index}>
                                        {`@${tag.value}`}
                                    </PartiText>
                                ) : null,
                            )}
                        </View>
                        <PartiText style={styles.header}>
                            {t('appCampaign.campaignStep.requiredHashtagsHeader')}
                        </PartiText>
                        <View style={styles.hashtagContainer}>
                            {igStoryRepostFirstStepData.captionHashtags?.map((tag, index) =>
                                tag?.value ? (
                                    <PartiText style={styles.hashtag} key={index}>
                                        {`#${tag.value}`}
                                    </PartiText>
                                ) : null,
                            )}
                        </View>
                    </Card>
                    {igStoryRepostFirstStepData.exampleMedia?.length ||
                    igStoryRepostFirstStepData.exampleMediaDescription ? (
                        <MediaPreview
                            files={igStoryRepostFirstStepData.exampleMedia}
                            caption={igStoryRepostFirstStepData.exampleMediaDescription}
                        />
                    ) : null}
                    <MediaPreview />
                </View>
            </SubmitPage>
        </Fragment>
    )
}

// ToDo: Move this to a single stylesheet to be reused by all preview components
const styles = StyleSheet.create({
    campaignCategoryItemContainer: {
        paddingVertical: spacing.extraSmall,
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    taskType: {
        fontSize: moderateScale(12),
        fontWeight: fontWeight.medium,
    },
    stepTitle: {
        fontSize: moderateScale(24),
        fontFamily: fontFamily.title,
        fontWeight: fontWeight.bold,
        paddingTop: spacing.medium,
        paddingBottom: spacing.medium2,
    },
    header: {
        fontFamily: fontFamily.title,
        fontSize: fontSize.medium,
        fontWeight: fontWeight.bold,
        paddingTop: spacing.small2,
        paddingBottom: spacing.extraSmall,
    },
    title: {
        fontSize: fontSize.extraSmall,
        paddingBottom: spacing.small,
        color: color.tundora,
        fontFamily: fontFamily.copy,
        fontWeight: fontWeight.medium,
    },
    footer: { fontSize: fontSize.small, paddingTop: spacing.small },
    container: {
        borderRadius: moderateScale(10),
        padding: spacing.medium,
        margin: 0,
        marginBottom: spacing.medium2,
    },
    divider: {
        width: '100%',
        height: moderateScale(1),
        borderWidth: moderateScale(1),
        borderStyle: 'solid',
        borderColor: color.silverChalice,
        marginVertical: spacing.small2,
    },
    hashtagContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: moderateScale(4),
        marginTop: moderateScale(4),
    },
    hashtag: {
        display: 'flex',
        alignItems: 'center',
        minHeight: moderateScale(24),
        borderWidth: moderateScale(1),
        borderStyle: 'solid',
        width: 'fit-content',
        borderRadius: moderateScale(4),
        borderColor: '#253778',
        paddingVertical: spacing.extraSmall,
        paddingHorizontal: spacing.small,
        color: '#253778',
        wordBreak: 'break-word',
    },
    requirementSectionHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: moderateScale(4),
    },
})

export default IGStoryRepostPreview
